import React from 'react';
import { Link } from 'react-router-dom';
import { Instagram, Youtube } from 'lucide-react';

// Custom X (Twitter) icon
const XIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

// Custom TikTok icon
const TikTokIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
  </svg>
);

const Footer = () => {
  const productLinks = [
    { path: '/', label: 'Home' },
    { path: '/how-it-works', label: 'How Levanta Works' },
    { path: '/levanta-story', label: "Levanta's Story" },
  ];

  const companyLinks = [
    { path: '/mission', label: 'Our Mission' },
    { path: '/vision', label: 'Our Vision' },
    { path: '/founder', label: 'About the Founder' },
    // { path: '/team', label: 'Team' },  // temporarily disabled
  ];

  const supportLinks = [
    { path: '/contact', label: 'Contact' },
    { path: '/faq', label: 'FAQ' },
  ];

  const contentLinks = [
    { path: '/media-press', label: 'Media & Press' },
    { path: '/blog', label: 'Blog' },
  ];

  const legalPages = [
    { path: '/privacy', label: 'Privacy Policy' },
    { path: '/terms', label: 'Terms of Service' }
  ];

  const socialLinks = [
    { 
      url: 'https://www.instagram.com/levanta.life/', 
      label: 'Instagram',
      icon: Instagram
    },
    { 
      url: 'https://www.tiktok.com/@levanta.life', 
      label: 'TikTok',
      icon: TikTokIcon
    },
    { 
      url: 'https://www.youtube.com/@Levanta_life', 
      label: 'YouTube',
      icon: Youtube
    },
    { 
      url: 'https://x.com/Levantaapp', 
      label: 'X',
      icon: XIcon
    }
  ];

  return (
    <footer className="bg-white border-t border-gray-200 mt-20">
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid grid-cols-2 md:grid-cols-12 gap-x-8 gap-y-10 mb-8">
          {/* Brand */}
          <div className="col-span-2 md:col-span-2">
            <div className="flex items-center gap-3 mb-4">
              <img 
                src="/favicon.png" 
                alt="Levanta Logo" 
                className="h-12 w-12 rounded-lg"
              />
              <img 
                src="https://customer-assets.emergentagent.com/job_fff9b75d-1f9a-47f7-9e37-9066bc0d7456/artifacts/qgfu5fs6_Untitled%20design%20%282%29.svg" 
                alt="LEVANTA" 
                className="h-4"
              />
            </div>
            <p className="text-sm text-gray-600 leading-relaxed mb-4">
              The Structured Growth System for Real Life.
            </p>
            {/* Social Media Links */}
            <div className="flex flex-wrap gap-3">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 bg-gray-100 hover:bg-blue-100 rounded-full flex items-center justify-center transition-colors"
                  aria-label={social.label}
                >
                  <social.icon className="w-4 h-4 text-gray-600 hover:text-blue-600" />
                </a>
              ))}
            </div>
          </div>

          {/* Product */}
          <div className="col-span-1 md:col-span-2">
            <h4 className="font-semibold text-gray-900 mb-4">Product</h4>
            <ul className="space-y-4">
              {productLinks.map((link) => (
                <li key={link.path}>
                  <Link to={link.path} className="text-sm text-gray-600 hover:text-blue-500">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div className="col-span-1 md:col-span-2">
            <h4 className="font-semibold text-gray-900 mb-4">Company</h4>
            <ul className="space-y-4">
              {companyLinks.map((link) => (
                <li key={link.path}>
                  <Link to={link.path} className="text-sm text-gray-600 hover:text-blue-500">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Download */}
          <div className="col-span-1 md:col-span-2">
            <h4 className="font-semibold text-gray-900 mb-4">Download</h4>
            <ul className="space-y-4">
              <li>
                <Link to="/download-app" className="text-sm text-gray-600 hover:text-blue-500">
                  Google Play
                </Link>
              </li>
              <li className="text-sm text-gray-400 cursor-not-allowed select-none">
                App Store{' '}
                <span className="ml-1 text-[10px] uppercase tracking-widest text-gray-400">soon</span>
              </li>
              <li className="text-sm text-gray-400 cursor-not-allowed select-none">
                Windows{' '}
                <span className="ml-1 text-[10px] uppercase tracking-widest text-gray-400">soon</span>
              </li>
            </ul>
          </div>

          {/* Support */}
          <div className="col-span-1 md:col-span-2">
            <h4 className="font-semibold text-gray-900 mb-4">Support</h4>
            <ul className="space-y-4">
              {supportLinks.map((link) => (
                <li key={link.path}>
                  <Link to={link.path} className="text-sm text-gray-600 hover:text-blue-500">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Content */}
          <div className="col-span-1 md:col-span-2">
            <h4 className="font-semibold text-gray-900 mb-4">Content</h4>
            <ul className="space-y-4">
              {contentLinks.map((link) => (
                <li key={link.path}>
                  <Link to={link.path} className="text-sm text-gray-600 hover:text-blue-500">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom - Copyright + Legal */}
        <div className="mt-8 pt-8 border-t border-gray-200 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-sm text-gray-600">
            © {new Date().getFullYear()} Levanta. All rights reserved.
          </p>
          <div className="flex gap-6">
            {legalPages.map((link) => (
              <Link 
                key={link.path} 
                to={link.path} 
                className="text-sm text-gray-600 hover:text-blue-500"
              >
                {link.label}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
